/** Resolve playback/poster URLs for a bound `video_asset` canvas node from
 * its persisted `entity_id` (composite library id) plus the lightweight
 * snapshot stored in `data.fields.videoAssetSnapshot`. */

import {
  videoAssetPlaybackUrl,
  videoAssetPosterUrl,
  type VideoAssetItem,
} from '../../../api'

export interface VideoAssetBindSnapshot {
  job_id?: string | null
  composite_id?: string | null
  upload_asset_id?: string | null
  root_video_id?: string | null
  width?: number | null
  height?: number | null
  duration_seconds?: number | null
}

export function videoAssetSnapshotFromItem(item: VideoAssetItem): VideoAssetBindSnapshot {
  return {
    job_id: item.job_id,
    composite_id: item.composite_id,
    upload_asset_id: item.upload_asset_id,
    root_video_id: item.root_video_id,
    width: item.width,
    height: item.height,
    duration_seconds: item.duration_seconds,
  }
}

export function videoAssetItemFromBinding(entityId: string, snapshot?: VideoAssetBindSnapshot | null): VideoAssetItem {
  const upload_asset_id = snapshot?.upload_asset_id ?? (entityId.startsWith('upload:') ? entityId.slice(7) : null)
  const job_id = snapshot?.job_id ?? (entityId.startsWith('job:') ? entityId.slice(4) : null)
  const composite_id = snapshot?.composite_id ?? (entityId.startsWith('composite:') ? entityId.slice(10) : null)
  const source_kind = upload_asset_id ? 'upload' : composite_id ? 'continuous_generated' : 'ai_generated'
  return {
    id: entityId,
    source_kind,
    title: '',
    description: null,
    duration_seconds: snapshot?.duration_seconds ?? null,
    width: snapshot?.width ?? null,
    height: snapshot?.height ?? null,
    size_bytes: null,
    created_at: null,
    is_continuation_segment: false,
    is_continuation_root: false,
    is_composite: !!composite_id,
    is_published: false,
    job_id,
    composite_id,
    upload_asset_id,
    filename: null,
    can_delete_hint: true,
    can_edit_meta: false,
    can_publish: false,
    root_video_id: snapshot?.root_video_id ?? undefined,
  }
}

/** Bare (<=36 char) id identity for a `VideoAssetItem`, safe to persist
 * into `CanvasNode.entity_id` (`VARCHAR(36)` column). `item.id` itself is
 * a "kind:<uuid>" composite presentation string (e.g. "upload:<uuid>" is
 * 43+ chars, "composite:<uuid>" is 46+) that overflows that column and
 * fails the whole canvas autosave PUT with a DB error the moment such a
 * node is saved. Every real consumer that needs the kind distinction
 * already reads it from `data.fields.videoAssetSnapshot` (see
 * `videoAssetItemFromBinding` above), never from entity_id's own prefix,
 * so substituting the bare id here is lossless. VistoeraTASK-00232 found
 * this via a real Preview network-response inspection (psycopg
 * StringDataRightTruncation on saas_canvas_nodes.entity_id) while
 * testing the new OS-drop video-asset path, and confirmed it also
 * affects the pre-existing VideoAssetPicker "select existing video"
 * bind flow the same way, since both write entity_id from `item.id`. */
export function bareVideoAssetEntityId(item: VideoAssetItem): string {
  if (item.job_id) return item.job_id
  if (item.root_video_id) return item.root_video_id
  if (item.composite_id) return item.composite_id
  if (item.upload_asset_id) return item.upload_asset_id
  return item.id
}

export function resolveBoundVideoPlaybackUrl(
  workspaceId: string,
  entityId: string,
  snapshot?: VideoAssetBindSnapshot | null,
): string {
  return videoAssetPlaybackUrl(workspaceId, videoAssetItemFromBinding(entityId, snapshot))
}

export function resolveBoundVideoPosterUrl(
  workspaceId: string,
  entityId: string,
  snapshot?: VideoAssetBindSnapshot | null,
): string | undefined {
  return videoAssetPosterUrl(workspaceId, videoAssetItemFromBinding(entityId, snapshot))
}
